// ============================================================================
// EmailVerifyBanner — a slim, dismissible app-wide nudge shown when the signed-in
// trainer has NO email on file, or an email that hasn't been verified yet. It
// links to /app/account where the email can be added / a verification link sent.
//
// • Reads the account via useAccount() (the same cache the Account page writes),
//   so the moment the email is confirmed (the verify-email route invalidates the
//   account query) the banner disappears with no reload.
// • Dismissal is per-session (sessionStorage), keyed by the unverified email, so
//   dismissing doesn't permanently hide it and a NEW/changed email re-surfaces it.
// • Never shown on the Account page itself (the page owns the full email section)
//   nor while the account is still loading.
// Built on the locked Pulse Banner; all copy via the `account` i18n namespace.
// ============================================================================
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Banner, Button } from "../components";
import { useAccount } from "../hooks";

const DISMISS_PREFIX = "vf.emailVerifyBanner.dismissed:";

export function EmailVerifyBanner() {
  const { t } = useTranslation("account");
  const { pathname } = useLocation();
  const account = useAccount();

  // The dismissal key tracks the CURRENT email (or "none" when missing) so a
  // change/add re-arms the banner even within the same browser session.
  const email = account.data?.email ?? "";
  const dismissKey = `${DISMISS_PREFIX}${email || "none"}`;

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(dismissKey) === "1";
    } catch {
      return false;
    }
  });

  // Nothing to nudge about: still loading, already verified, or on the page
  // that owns the full email section.
  if (!account.data) return null;
  if (account.data.email_verified) return null;
  if (pathname.startsWith("/app/account")) return null;
  if (dismissed) return null;

  const hasEmail = Boolean(account.data.email);

  const onDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* sessionStorage unavailable (private mode) — dismiss for this mount only */
    }
  };

  return (
    <div className="mx-auto max-w-shell px-gutter pt-6">
      <Banner
        tone="warning"
        title={hasEmail ? t("emailBanner.unverifiedTitle") : t("emailBanner.missingTitle")}
        onDismiss={onDismiss}
        action={
          <Link to="/app/account">
            <Button size="sm" variant="secondary">
              {hasEmail ? t("emailBanner.verifyCta") : t("emailBanner.addCta")}
            </Button>
          </Link>
        }
      >
        {hasEmail
          ? t("emailBanner.unverifiedBlurb", { email: account.data.email })
          : t("emailBanner.missingBlurb")}
      </Banner>
    </div>
  );
}
